import { useImageCrop } from '@/components/image-crop'
import RichTextEditor from '@/components/rich-text-editor'
import Editor from '@monaco-editor/react'
import { THEME_ICONS, type ThemeField, type ThemeScalarField } from '@mediardex/contracts'
import { useState } from 'react'
import { MdAdd, MdArrowDownward, MdArrowUpward, MdDelete, MdImage, MdUpload } from 'react-icons/md'

/** Listas de que os campos de referência precisam (carregadas uma vez pelo editor). */
export interface ThemeFieldOptions {
    widgets: { id: number; name: string }[]
    categories: { id: number; name: string }[]
    collections: { id: number; name: string }[]
    /** Envia imagem ou vídeo para o storage e devolve a URL pública. */
    uploadMedia: (file: File) => Promise<string | null>
}

interface ThemeFieldProps {
    name: string
    field: ThemeField
    value: unknown
    onChange: (value: unknown) => void
    options: ThemeFieldOptions
}

/** Rótulo, campo e ajuda com o mesmo espaçamento em todos os tipos. */
function FieldShell({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-base-content">{label}</span>
            {children}
            {help && <span className="text-xs text-base-content/60">{help}</span>}
        </div>
    )
}

function ImageField({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: ThemeFieldOptions }) {
    const [uploading, setUploading] = useState(false)
    // Cada imagem do tema tem um lugar diferente (banner largo, bloco do mosaico,
    // destaque do mega menu), então o enquadramento é livre com atalhos de proporção.
    const { cropFile, cropDialog } = useImageCrop({
        aspects: [
            { label: 'Livre' },
            { label: 'Paisagem (16:9)', value: 16 / 9 },
            { label: 'Paisagem (4:3)', value: 4 / 3 },
            { label: 'Quadrada', value: 1 },
            { label: 'Retrato (3:4)', value: 3 / 4 },
        ],
        output: { maxWidth: 1600 },
    })

    const handleFile = async (file: File | undefined) => {
        if (!file) return
        const cropped = await cropFile(file)
        if (!cropped) return
        setUploading(true)
        try {
            const url = await options.uploadMedia(cropped)
            if (url) onChange(url)
        } finally {
            setUploading(false)
        }
    }

    return (
        <div className="flex flex-col gap-2">
            {value ? (
                <div className="overflow-hidden rounded-lg border border-base-300 bg-base-200">
                    <img src={value} alt="" className="max-h-40 w-full object-contain" />
                </div>
            ) : (
                <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-base-300 bg-base-200 text-base-content/40">
                    <MdImage className="text-3xl" />
                </div>
            )}
            <div className="flex gap-2">
                <label className={`btn btn-sm btn-outline flex-1 ${uploading ? 'btn-disabled' : ''}`}>
                    {uploading ? <span className="loading loading-spinner loading-xs" /> : <MdUpload />}
                    {value ? 'Trocar imagem' : 'Enviar imagem'}
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                            handleFile(e.target.files?.[0])
                            e.target.value = ''
                        }}
                    />
                </label>
                {value && (
                    <button type="button" className="btn btn-sm btn-ghost text-error" onClick={() => onChange('')}>
                        Remover
                    </button>
                )}
            </div>
            {cropDialog}
        </div>
    )
}

function VideoField({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: ThemeFieldOptions }) {
    const [uploading, setUploading] = useState(false)
    const isLink = /youtu\.?be|vimeo\.com/i.test(value)

    const handleFile = async (file: File | undefined) => {
        if (!file) return
        setUploading(true)
        try {
            const url = await options.uploadMedia(file)
            if (url) onChange(url)
        } finally {
            setUploading(false)
        }
    }

    return (
        <div className="flex flex-col gap-2">
            {value && !isLink && (
                <video src={value} controls className="max-h-48 w-full rounded-lg border border-base-300 bg-black" />
            )}
            <input
                type="text"
                className="input input-bordered w-full"
                placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..."
                value={value}
                onChange={(e) => onChange(e.target.value.trim())}
            />
            <div className="flex gap-2">
                <label className={`btn btn-sm btn-outline flex-1 ${uploading ? 'btn-disabled' : ''}`}>
                    {uploading ? <span className="loading loading-spinner loading-xs" /> : <MdUpload />}
                    Enviar arquivo de vídeo
                    <input
                        type="file"
                        accept="video/mp4,video/webm"
                        className="hidden"
                        onChange={(e) => {
                            handleFile(e.target.files?.[0])
                            e.target.value = ''
                        }}
                    />
                </label>
                {value && (
                    <button type="button" className="btn btn-sm btn-ghost text-error" onClick={() => onChange('')}>
                        Remover
                    </button>
                )}
            </div>
        </div>
    )
}

function ScalarInput({
    field,
    value,
    onChange,
    options,
}: {
    field: ThemeScalarField
    value: unknown
    onChange: (value: unknown) => void
    options: ThemeFieldOptions
}) {
    switch (field.type) {
        case 'text':
            return field.multiline ? (
                <textarea
                    className="textarea textarea-bordered w-full"
                    rows={3}
                    maxLength={field.maxLength}
                    value={(value as string) ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            ) : (
                <input
                    type="text"
                    className="input input-bordered w-full"
                    maxLength={field.maxLength}
                    value={(value as string) ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            )
        case 'richtext':
            return (
                <RichTextEditor
                    content={(value as string) ?? ''}
                    onChange={(html) => onChange(html)}
                    minHeight="220px"
                    onImageUpload={async (file) => {
                        const url = await options.uploadMedia(file)
                        if (!url) throw new Error('Não foi possível enviar a imagem')
                        return url
                    }}
                />
            )
        case 'html':
            return (
                <div className="overflow-hidden rounded-lg border border-base-300">
                    <Editor
                        height="420px"
                        language="html"
                        value={(value as string) ?? ''}
                        onChange={(html) => onChange(html ?? '')}
                        theme="vs-dark"
                        options={{
                            minimap: { enabled: false },
                            fontSize: 14,
                            lineNumbers: 'on',
                            scrollBeyondLastLine: false,
                            wordWrap: 'on',
                            automaticLayout: true,
                            tabSize: 2,
                            padding: { top: 12 },
                        }}
                    />
                </div>
            )
        case 'image':
            return <ImageField value={(value as string) ?? ''} onChange={onChange} options={options} />
        case 'video':
            return <VideoField value={(value as string) ?? ''} onChange={onChange} options={options} />
        case 'icon':
            return (
                <select
                    className="select select-bordered w-full"
                    value={(value as string) || field.default}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {Object.entries(THEME_ICONS).map(([key, label]) => (
                        <option key={key} value={key}>
                            {label}
                        </option>
                    ))}
                </select>
            )
        case 'color':
            return (
                <div className="flex items-center gap-3">
                    <input
                        type="color"
                        className="h-10 w-14 cursor-pointer rounded-lg border border-base-300 bg-transparent p-1"
                        value={(value as string) || field.default}
                        onChange={(e) => onChange(e.target.value.toUpperCase())}
                    />
                    <input
                        type="text"
                        className="input input-bordered w-32 font-mono uppercase"
                        maxLength={7}
                        value={(value as string) ?? ''}
                        onChange={(e) => onChange(e.target.value)}
                    />
                </div>
            )
        case 'font':
            return (
                <select
                    className="select select-bordered w-full"
                    value={(value as string) ?? field.default}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {field.inheritLabel && <option value="">{field.inheritLabel}</option>}
                    {field.options.map((font) => (
                        <option key={font} value={font} style={{ fontFamily: font }}>
                            {font}
                        </option>
                    ))}
                </select>
            )
        case 'select':
            return (
                <select
                    className="select select-bordered w-full"
                    value={(value as string) ?? field.default}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {field.options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            )
        case 'toggle':
            return (
                <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={Boolean(value)}
                    onChange={(e) => onChange(e.target.checked)}
                />
            )
        case 'range': {
            const current = typeof value === 'number' ? value : field.default
            return (
                <div className="flex items-center gap-3">
                    <input
                        type="range"
                        className="range range-primary range-sm flex-1"
                        min={field.min}
                        max={field.max}
                        step={field.step ?? 1}
                        value={current}
                        onChange={(e) => onChange(Number(e.target.value))}
                    />
                    <span className="w-14 text-right text-sm tabular-nums">
                        {current}
                        {field.unit ?? ''}
                    </span>
                </div>
            )
        }
        case 'link':
            return (
                <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="/produtos ou https://..."
                    value={(value as string) ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            )
        case 'widget':
            return (
                <select
                    className="select select-bordered w-full"
                    value={typeof value === 'number' ? value : ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
                >
                    <option value="">Nenhum</option>
                    {options.widgets.map((widget) => (
                        <option key={widget.id} value={widget.id}>
                            {widget.name}
                        </option>
                    ))}
                </select>
            )
        case 'category':
            return (
                <select
                    className="select select-bordered w-full"
                    value={typeof value === 'number' ? value : ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
                >
                    <option value="">Todas as categorias</option>
                    {options.categories.map((category) => (
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>
                    ))}
                </select>
            )
        case 'collection':
            return (
                <select
                    className="select select-bordered w-full"
                    value={typeof value === 'number' ? value : ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
                >
                    <option value="">{options.collections.length ? 'Escolha uma coleção' : 'Nenhuma coleção criada'}</option>
                    {options.collections.map((collection) => (
                        <option key={collection.id} value={collection.id}>
                            {collection.name}
                        </option>
                    ))}
                </select>
            )
    }
}

/**
 * Nome do item de uma lista no editor: o que ele é (a opção escolhida no primeiro campo de
 * seleção, ex.: "Sobre a loja") ou o primeiro texto preenchido (ex.: "Meus pedidos").
 */
function itemTitle(field: Extract<ThemeField, { type: 'list' }>, item: Record<string, unknown>, index: number): string {
    for (const [key, sub] of Object.entries(field.of)) {
        const value = item[key]
        if (sub.type === 'select') {
            const label = sub.options.find((option) => option.value === value)?.label
            if (label) return label
        }
        if (sub.type === 'text' && typeof value === 'string' && value.trim()) return value.trim()
    }
    return `Item ${index + 1}`
}

/** Campos de um grupo que aparecem com os valores atuais (regra `visibleWhen`). */
function visibleEntries(fields: Record<string, ThemeField>, values: Record<string, unknown>): [string, ThemeField][] {
    return Object.entries(fields).filter(([, field]) => {
        const rule = field.visibleWhen
        if (!rule) return true
        const current = values[rule.field] ?? (fields[rule.field] as { default?: unknown } | undefined)?.default
        return current === rule.equals
    })
}

/** Campo do manifesto: escalar ou lista de itens. */
export default function ThemeFieldInput({ name, field, value, onChange, options }: ThemeFieldProps) {
    if (field.type !== 'list') {
        // Liga/desliga fica na mesma linha do rótulo.
        if (field.type === 'toggle') {
            return (
                <label className="flex cursor-pointer items-center justify-between gap-4">
                    <span className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">{field.label}</span>
                        {field.help && <span className="text-xs text-base-content/60">{field.help}</span>}
                    </span>
                    <ScalarInput field={field} value={value} onChange={onChange} options={options} />
                </label>
            )
        }
        return (
            <FieldShell label={field.label} help={field.help}>
                <ScalarInput field={field} value={value} onChange={onChange} options={options} />
            </FieldShell>
        )
    }

    const items = Array.isArray(value) ? (value as Record<string, unknown>[]) : []
    const update = (next: Record<string, unknown>[]) => onChange(next)
    const move = (index: number, delta: number) => {
        const next = [...items]
        const [item] = next.splice(index, 1)
        next.splice(index + delta, 0, item)
        update(next)
    }

    return (
        <FieldShell label={field.label} help={field.help}>
            <div className="flex flex-col gap-3">
                {items.map((item, index) => (
                    <div key={`${name}-${index}`} className="rounded-lg border border-base-300 p-3">
                        <div className="mb-3 flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase text-base-content/60">{itemTitle(field, item, index)}</span>
                            <div className="flex gap-1">
                                <button type="button" className="btn btn-ghost btn-xs" disabled={index === 0} onClick={() => move(index, -1)} title="Subir">
                                    <MdArrowUpward />
                                </button>
                                <button type="button" className="btn btn-ghost btn-xs" disabled={index === items.length - 1} onClick={() => move(index, 1)} title="Descer">
                                    <MdArrowDownward />
                                </button>
                                <button type="button" className="btn btn-ghost btn-xs text-error" onClick={() => update(items.filter((_, i) => i !== index))} title="Remover">
                                    <MdDelete />
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col gap-4">
                            {visibleEntries(field.of, item).map(([key, subField]) => (
                                <ThemeFieldInput
                                    key={key}
                                    name={`${name}.${index}.${key}`}
                                    field={subField}
                                    value={item[key]}
                                    onChange={(next) => update(items.map((it, i) => (i === index ? { ...it, [key]: next } : it)))}
                                    options={options}
                                />
                            ))}
                        </div>
                    </div>
                ))}
                {items.length < field.max && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() =>
                            // Item novo com os padrões dos campos (ícone, nota...).
                            update([
                                ...items,
                                Object.fromEntries(
                                    Object.entries(field.of).map(([key, sub]) => [
                                        key,
                                        'default' in sub && sub.default !== undefined ? sub.default : '',
                                    ]),
                                ),
                            ])
                        }
                    >
                        <MdAdd /> Adicionar item
                    </button>
                )}
            </div>
        </FieldShell>
    )
}

/** Campo que precisa da linha inteira (não cabe numa coluna da grade). */
function isWideField(field: ThemeField): boolean {
    return (
        field.type === 'richtext' ||
        field.type === 'html' ||
        field.type === 'list' ||
        field.type === 'image' ||
        field.type === 'video' ||
        (field.type === 'text' && !!field.multiline)
    )
}

/**
 * Campos de um grupo (configurações do tema ou de uma seção) em grade: as cores lado a
 * lado numa faixa própria, os demais em duas colunas, e os largos na linha inteira.
 */
export function ThemeFieldGrid({
    idPrefix,
    fields,
    values,
    onChange,
    options,
}: {
    idPrefix: string
    fields: Record<string, ThemeField>
    values: Record<string, unknown>
    onChange: (key: string, value: unknown) => void
    options: ThemeFieldOptions
}) {
    // Campo condicionado a outro (`visibleWhen`) só aparece quando a condição vale.
    const entries = visibleEntries(fields, values)

    // Campos do mesmo assunto (`group`) juntos, na ordem em que o assunto aparece; os sem
    // assunto ficam no topo.
    const groups: { title: string; entries: [string, ThemeField][] }[] = []
    for (const entry of entries) {
        const title = entry[1].group ?? ''
        const group = groups.find((g) => g.title === title)
        if (group) group.entries.push(entry)
        else if (title) groups.push({ title, entries: [entry] })
        else groups.unshift({ title, entries: [entry] })
    }

    const render = ([key, field]: [string, ThemeField]) => (
        <ThemeFieldInput
            key={`${idPrefix}-${key}`}
            name={`${idPrefix}.${key}`}
            field={field}
            value={values[key]}
            options={options}
            onChange={(value) => onChange(key, value)}
        />
    )

    const renderBlock = (blockEntries: [string, ThemeField][]) => {
        const colors = blockEntries.filter(([, field]) => field.type === 'color')
        const others = blockEntries.filter(([, field]) => field.type !== 'color')
        return (
            <>
                {colors.length > 0 && (
                    <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
                        {colors.map(render)}
                    </div>
                )}
                {others.length > 0 && (
                    <div className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2">
                        {others.map((entry) => (
                            <div key={`${idPrefix}-${entry[0]}-cell`} className={isWideField(entry[1]) ? 'md:col-span-2' : ''}>
                                {render(entry)}
                            </div>
                        ))}
                    </div>
                )}
            </>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {groups.map((group) =>
                group.title ? (
                    <section key={`${idPrefix}-group-${group.title}`} className="flex flex-col gap-5 border-t border-base-200 pt-5">
                        <h4 className="text-sm font-semibold text-base-content/80">{group.title}</h4>
                        {renderBlock(group.entries)}
                    </section>
                ) : (
                    <div key={`${idPrefix}-group-top`} className="flex flex-col gap-6">
                        {renderBlock(group.entries)}
                    </div>
                ),
            )}
        </div>
    )
}
